import type {
  ActionRequirement,
  MidnightOfferRootRequirement,
  RequirementSignature,
  SignatureRequirement,
  Transaction
} from '@morpho-org/morpho-sdk'

import { isRequirementSignature } from '@morpho-org/morpho-sdk'
import { assertNever } from '@repo/utils'
import type { SignatureRequest } from '@repo/web3'
import { type Address, type Hex, walletActions } from 'viem'
import { z } from 'zod'

import { addressSchema } from '@/lib/orpc/schemas'

import type { ActionFlowSource, ActionFlowSourceCall } from './buildActionFlow'

export type VaultActionRequirement = Exclude<ActionRequirement, MidnightOfferRootRequirement>

const permitTypedDataSchema = z.object({
  domain: z.object({ verifyingContract: addressSchema }),
  message: z.object({ owner: addressSchema, nonce: z.bigint() })
})

const permit2SignatureTransferTypedDataSchema = z.object({
  message: z.object({ permitted: z.object({ token: addressSchema }) })
})

const authorizationTypedDataSchema = z.object({
  message: z.object({ authorizer: addressSchema, nonce: z.bigint() })
})

interface MorphoSdkTransaction {
  readonly requirements: readonly VaultActionRequirement[]
  readonly buildTx: (
    signatures: readonly RequirementSignature[]
  ) => Pick<Transaction, 'to' | 'value' | 'data'>
}

interface GetMorphoSdkActionFlowSourceParameters {
  transaction: MorphoSdkTransaction
  account: Address
  getRequirementLabel: (requirement: VaultActionRequirement) => string
}

export function getMorphoSdkActionFlowSource({
  transaction,
  account,
  getRequirementLabel
}: GetMorphoSdkActionFlowSourceParameters): ActionFlowSource {
  const signedRequirementFactories: ((signature: Hex) => RequirementSignature)[] = []
  const signatures: SignatureRequest[] = []
  const requirements: ActionFlowSourceCall[] = []

  for (const requirement of transaction.requirements) {
    if (isRequirementSignature(requirement)) {
      signedRequirementFactories.push(getSignedRequirement(requirement, account))
      signatures.push({
        label: getRequirementLabel(requirement),
        sign: async client => {
          const signed = await requirement.sign(client.extend(walletActions), account)
          return signed.args.signature
        }
      })
      continue
    }

    requirements.push({
      label: getRequirementLabel(requirement),
      call: toResolvedCall(requirement)
    })
  }

  return {
    signatures,
    requirements,
    buildTransaction: signatures => {
      if (signatures.length !== signedRequirementFactories.length) {
        throw new Error(
          'Cannot build transaction with a different number of signatures than requirements'
        )
      }

      return toResolvedCall(
        transaction.buildTx(
          signedRequirementFactories.map((buildSignedRequirement, index) => {
            const signature = signatures[index]
            if (signature === undefined) {
              throw new Error('Cannot build transaction before every signature is collected')
            }

            return buildSignedRequirement(signature)
          })
        )
      )
    }
  }
}

function getSignedRequirement(
  requirement: Exclude<SignatureRequirement, MidnightOfferRootRequirement>,
  account: Address
): (signature: Hex) => RequirementSignature {
  const { action } = requirement

  switch (action.type) {
    case 'permit': {
      const typedData = permitTypedDataSchema.parse(action.typedData)

      return (signature: Hex) => ({
        action,
        args: {
          owner: typedData.message.owner,
          signature,
          deadline: action.args.deadline,
          amount: action.args.amount,
          asset: typedData.domain.verifyingContract,
          nonce: typedData.message.nonce
        }
      })
    }
    case 'permit2SignatureTransfer': {
      const typedData = permit2SignatureTransferTypedDataSchema.parse(action.typedData)

      return (signature: Hex) => ({
        action,
        args: {
          owner: account,
          asset: typedData.message.permitted.token,
          amount: action.args.amount,
          nonce: action.args.nonce,
          deadline: action.args.deadline,
          signature
        }
      })
    }
    case 'authorization': {
      const typedData = authorizationTypedDataSchema.parse(action.typedData)

      return (signature: Hex) => ({
        action,
        args: {
          owner: typedData.message.authorizer,
          authorized: action.args.authorized,
          isAuthorized: action.args.isAuthorized,
          nonce: typedData.message.nonce,
          deadline: action.args.deadline,
          signature
        }
      })
    }
    default:
      return assertNever(action)
  }
}

function toResolvedCall({ to, value, data }: Pick<Transaction, 'to' | 'value' | 'data'>) {
  return { to, value, data }
}
